/**
 * Authentication Interceptor
 * Handles authentication and authorization for API requests
 */

import {ApiError, ErrorInterceptor, RequestInterceptor} from '../types';
import {logout} from '@/store/slices/authSlice';

/**
 * Creates a request interceptor that adds authentication headers
 * @param getState - Function to get the current state
 * @returns Request interceptor function
 */
export const createAuthRequestInterceptor = (getState: () => any): RequestInterceptor => {
  return async (config) => {
    // Get the auth state from the Redux store
    const authState = getState().auth;
    const user = authState.user;

    // If we have a token, add it to the headers
    if (user?.accessToken) {
      const tokenType = user.tokenType || 'Bearer';
      const authHeader = `${tokenType} ${user.accessToken}`;
      
      config.headers = {
        ...config.headers,
        Authorization: authHeader,
      };
    }

    return config;
  };
};

/**
 * Creates an error interceptor that handles authentication errors
 * @param dispatch - Redux dispatch function
 * @param onAuthError - Optional callback for auth errors (e.g., for redirecting)
 * @returns Error interceptor function
 */
export const createAuthErrorInterceptor = (
  dispatch: (action: any) => void,
  onAuthError?: (error: ApiError) => void
): ErrorInterceptor => {
  return async (error) => {
    // Handle authentication errors (401 Unauthorized)
    if (error.status === 401) {
      // Dispatch logout action to clear auth state
      dispatch(logout());
      
      // Call the optional callback (e.g., for redirecting to login page)
      if (onAuthError) {
        onAuthError(error);
      }
    }
    
    // Handle authorization errors (403 Forbidden)
    if (error.status === 403) {
      // Call the optional callback (e.g., for showing permission denied message)
      if (onAuthError) {
        onAuthError(error);
      }
    }
    
    // Return the error to be handled by the next interceptor or thrown
    return error;
  };
};
